import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Calculator, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getInstalaldoresComelit, addProposta } from '@/lib/index';
import type { InstaladorComelit } from '@/lib/index';

const LABOR: Record<string, { video: number; audio: number }> = {
  '1-10':  { video: 430,  audio: 380 },
  '11-15': { video: 600,  audio: 550 },
  '16-20': { video: 650,  audio: 600 },
  '21-25': { video: 760,  audio: 730 },
  '26-30': { video: 850,  audio: 800 },
  '31-35': { video: 980,  audio: 900 },
  '36-40': { video: 1050, audio: 990 },
  '41-45': { video: 1200, audio: 1100 },
  '46-50': { video: 1320, audio: 1200 },
};

function getRange(n: number): string {
  if (n <= 10)  return '1-10';
  if (n <= 15)  return '11-15';
  if (n <= 20)  return '16-20';
  if (n <= 25)  return '21-25';
  if (n <= 30)  return '26-30';
  if (n <= 35)  return '31-35';
  if (n <= 40)  return '36-40';
  if (n <= 45)  return '41-45';
  if (n <= 50)  return '46-50';
  return 'mais50';
}

function calcLabor(n: number, tipo: 'video' | 'audio'): number {
  if (n <= 50) {
    const r = getRange(n);
    return LABOR[r][tipo];
  }
  return 1320 + (n - 50) * 50;
}

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

interface Row { desc: string; qty: number; unit: number }

export default function ProposalGenerator() {
  const navigate = useNavigate();
  const [installer, setInstaller] = useState<InstaladorComelit | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const sid = localStorage.getItem('installerSession');
    if (sid) getInstalaldoresComelit().then(list => {
      const found = list.find(i => i.id === sid);
      if (found) {
        setInstaller(found);
        // Pré-preencher dados do instalador
        setInsNome(found.nome || '');
        setInsTel(found.telefone || '');
        setInsEmail(found.email || '');
        setInsNif(found.nif || '');
        setInsMorada(found.morada || '');
        setInsNumCliente(found.numeroClienteContera || '');
      }
    });
  }, []);

  // Dados Instalador
  const [insNome, setInsNome] = useState('');
  const [insNif, setInsNif] = useState('');
  const [insMorada, setInsMorada] = useState('');
  const [insCp, setInsCp] = useState('');
  const [insTel, setInsTel] = useState('');
  const [insEmail, setInsEmail] = useState('');
  const [insNumCliente, setInsNumCliente] = useState('');
  const [insCertif, setInsCertif] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  function handleLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setLogoDataUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  // Dados Obra
  const [ref, setRef] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteEmail, setClienteEmail] = useState('');
  const [obraMorada, setObraMorada] = useState('');
  const [obraLocalidade, setObraLocalidade] = useState('');
  const [tipoImovel, setTipoImovel] = useState('Prédio / Apartamento');
  const [dataVisita, setDataVisita] = useState('');
  const [dataProposta, setDataProposta] = useState(new Date().toISOString().split('T')[0]);
  const [validadeAte, setValidadeAte] = useState('');

  // Inquilinos / tipo
  const [numInquilinos, setNumInquilinos] = useState('');
  const [tipoSistema, setTipoSistema] = useState<'video' | 'audio'>('video');

  // Itens
  const [rows, setRows] = useState<Row[]>([
    { desc: '', qty: 1, unit: 0 },
    { desc: '', qty: 1, unit: 0 },
    { desc: '', qty: 1, unit: 0 },
  ]);

  const [notas, setNotas] = useState('');

  const laborSugerida = numInquilinos ? calcLabor(parseInt(numInquilinos) || 0, tipoSistema) : null;

  const subtotal = rows.reduce((s, r) => s + r.qty * r.unit, 0);
  const iva = subtotal * 0.23;
  const total = subtotal + iva;

  function updateRow(i: number, field: keyof Row, val: string) {
    const r = [...rows];
    if (field === 'desc') r[i].desc = val;
    else r[i][field] = parseFloat(val) || 0;
    setRows(r);
  }

  function applyLabor() {
    if (!laborSugerida) return;
    const r = [...rows];
    r[0] = { desc: `Mão de obra — ${tipoSistema === 'video' ? 'Vídeo' : 'Áudio'} (${numInquilinos} inquilinos)`, qty: 1, unit: laborSugerida };
    setRows(r);
  }

  const [refError, setRefError] = useState('');

  async function generate() {
    if (!ref.trim().toUpperCase().startsWith('CTR-')) {
      setRefError('O código CTR é obrigatório. Ex: CTR-3F255125');
      return;
    }
    setRefError('');
    setSaving(true);
    const d = (s: string) => s || '—';
    const rowsHtml = rows.filter(r => r.desc).map((r, i) => `
      <tr>
        <td style="padding:6px 8px;text-align:center;border:1px solid #ddd">${i + 1}</td>
        <td style="padding:6px 8px;border:1px solid #ddd">${r.desc}</td>
        <td style="padding:6px 8px;text-align:center;border:1px solid #ddd">${r.qty}</td>
        <td style="padding:6px 8px;text-align:right;border:1px solid #ddd">${fmt(r.unit)}</td>
        <td style="padding:6px 8px;text-align:right;border:1px solid #ddd;font-weight:600">${fmt(r.qty * r.unit)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html lang="pt"><head><meta charset="UTF-8">
    <title>Proposta ${d(ref)}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;font-size:11px;color:#222;padding:20px;max-width:800px;margin:auto}
      .header{display:flex;justify-content:space-between;align-items:flex-start;background:#1a3a6b;color:#fff;padding:16px 20px;border-radius:6px 6px 0 0}
      .header h1{font-size:16px;letter-spacing:1px}
      .header .right{text-align:right;font-size:10px;opacity:.85;line-height:1.6}
      .badge{display:inline-block;background:#e8f0fb;color:#1a3a6b;border:1px solid #c5d8f5;border-radius:4px;padding:3px 10px;font-size:10px;font-weight:700;margin-bottom:8px}
      .section{margin-top:14px}
      .section-title{background:#f0f4fa;border-left:3px solid #1a3a6b;padding:5px 10px;font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#1a3a6b;margin-bottom:8px}
      .grid2{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px}
      .field label{font-size:9px;color:#888;text-transform:uppercase;display:block;margin-bottom:1px}
      .field span{font-size:11px;font-weight:600}
      table{width:100%;border-collapse:collapse;margin-top:4px}
      thead tr{background:#1a3a6b;color:#fff}
      thead th{padding:7px 8px;font-size:10px;text-align:left}
      thead th:last-child,thead th:nth-child(3),thead th:nth-child(4){text-align:right}
      .totals{margin-top:10px;display:flex;justify-content:flex-end}
      .totals table{width:260px}
      .totals td{padding:4px 8px;border:1px solid #ddd;font-size:11px}
      .totals .total-row{background:#1a3a6b;color:#fff;font-weight:700;font-size:12px}
      .guarantee{margin-top:14px;background:#e8f5e9;border:1px solid #a5d6a7;border-radius:6px;padding:10px 14px}
      .guarantee h4{color:#2e7d32;font-size:10px;text-transform:uppercase;margin-bottom:4px}
      .guarantee p{font-size:10px;color:#1b5e20;line-height:1.5}
      .payment-info{margin-top:10px;background:#fff3e0;border:1px solid #ffcc80;border-radius:6px;padding:10px 14px}
      .payment-info h4{color:#e65100;font-size:10px;text-transform:uppercase;margin-bottom:4px}
      .payment-info p{font-size:10px;color:#bf360c;line-height:1.5}
      .signatures{margin-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:30px}
      .sig-box{border-top:1px solid #aaa;padding-top:6px;font-size:10px;color:#555}
      .footer{margin-top:20px;border-top:1px solid #ddd;padding-top:8px;text-align:center;font-size:9px;color:#888}
      @media print{body{padding:0}button{display:none}}
    </style></head><body>
    <div class="header">
      <div style="display:flex;align-items:center;gap:14px">
        ${logoDataUrl ? `<img src="${logoDataUrl}" style="height:52px;width:auto;border-radius:6px;background:#fff;padding:3px;object-fit:contain" alt="Logo" />` : ''}
        <div>
          <div style="font-size:10px;opacity:.7;margin-bottom:2px">INSTALADOR CERTIFICADO COMELIT</div>
          <h1>PROPOSTA DE ORÇAMENTO</h1>
          ${insNome ? `<div style="font-size:12px;font-weight:700;margin-top:3px;opacity:.95">${insNome}</div>` : ''}
          <div style="font-size:10px;margin-top:3px">Ref: <strong>${d(ref)}</strong></div>
        </div>
      </div>
      <div class="right">
        <div style="font-size:14px;font-weight:700">AT CONTERA</div>
        <div>Distribuidor Comelit Oficial</div>
        <div>66 anos de experiência</div>
        <div>comercial@atcontera.pt</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Dados da Empresa de Instalação</div>
      <div class="grid2">
        <div class="field"><label>Nome</label><span>${d(insNome)}</span></div>
        <div class="field"><label>NIF</label><span>${d(insNif)}</span></div>
        <div class="field"><label>Morada</label><span>${d(insMorada)}</span></div>
        <div class="field"><label>Código Postal</label><span>${d(insCp)}</span></div>
        <div class="field"><label>Telefone</label><span>${d(insTel)}</span></div>
        <div class="field"><label>Email</label><span>${d(insEmail)}</span></div>
        <div class="field"><label>Nº Cliente AT CONTERA</label><span>${d(insNumCliente)}</span></div>
        <div class="field"><label>Nº Certificação Comelit</label><span>${d(insCertif)}</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Dados da Obra</div>
      <div class="grid2">
        <div class="field"><label>Referência AT CONTERA</label><span style="color:#1a3a6b">${d(ref)}</span></div>
        <div class="field"><label>Cliente</label><span>${d(clienteNome)}</span></div>
        <div class="field"><label>Morada da Obra</label><span>${d(obraMorada)}</span></div>
        <div class="field"><label>Localidade</label><span>${d(obraLocalidade)}</span></div>
        <div class="field"><label>Tipo de Imóvel</label><span>${d(tipoImovel)}</span></div>
        <div class="field"><label>Sistema</label><span>${tipoSistema === 'video' ? '📹 Vídeo' : '🔊 Áudio'}${numInquilinos ? ` · ${numInquilinos} inquilinos` : ''}</span></div>
        <div class="field"><label>Data da Visita</label><span>${dataVisita ? new Date(dataVisita + 'T12:00:00').toLocaleDateString('pt-PT') : '—'}</span></div>
        <div class="field"><label>Data da Proposta</label><span>${dataProposta ? new Date(dataProposta + 'T12:00:00').toLocaleDateString('pt-PT') : '—'}</span></div>
        ${validadeAte ? `<div class="field"><label>Válida até</label><span>${new Date(validadeAte + 'T12:00:00').toLocaleDateString('pt-PT')}</span></div>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">Descritivo dos Trabalhos e Materiais</div>
      <table>
        <thead><tr>
          <th style="width:40px">Nº</th>
          <th>Descrição</th>
          <th style="width:50px;text-align:center">Qtd.</th>
          <th style="width:90px;text-align:right">Preço Unit.</th>
          <th style="width:90px;text-align:right">Total</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="totals"><table>
        <tr><td>Subtotal</td><td style="text-align:right">${fmt(subtotal)}</td></tr>
        <tr><td>IVA (23%)</td><td style="text-align:right">${fmt(iva)}</td></tr>
        <tr class="total-row"><td>TOTAL</td><td style="text-align:right">${fmt(total)}</td></tr>
      </table></div>
    </div>

    ${notas ? `<div class="section"><div class="section-title">Observações</div><p style="font-size:11px;line-height:1.6;padding:6px 8px;background:#f9f9f9;border-radius:4px;border:1px solid #eee">${notas}</p></div>` : ''}

    <div class="payment-info">
      <h4>⚠ Condições de Pagamento — Tudo pela AT CONTERA</h4>
      <p>• 30% de sinal pago pelo cliente à AT CONTERA na adjudicação &nbsp;|&nbsp; AT CONTERA fornece o material ao instalador<br>
      • 70% restantes pagos pelo cliente à AT CONTERA na conclusão da obra &nbsp;|&nbsp; AT CONTERA paga ao instalador em ≤48 horas<br>
      • Nunca pague ou receba dinheiro diretamente do/para o instalador. Este serviço é 100% gratuito e transparente.</p>
    </div>

    <div class="guarantee">
      <h4>🛡 Garantia AT CONTERA — 3 Anos</h4>
      <p>Todos os trabalhos realizados ao abrigo desta proposta estão cobertos pela Garantia Oficial AT CONTERA de 3 anos, incluindo mão de obra e materiais fornecidos. Instalação supervisionada pela AT CONTERA com Instalador Certificado Comelit.</p>
    </div>

    <div class="signatures">
      <div class="sig-box"><p>Assinatura do Instalador Certificado</p><br><br><br><p>Data: ___ / ___ / _______</p></div>
      <div class="sig-box"><p>Assinatura do Cliente / Aceitação</p><br><br><br><p>Data: ___ / ___ / _______</p></div>
    </div>

    <div class="footer">AT CONTERA · Distribuidor Oficial Comelit · 66 anos de experiência · comercial@atcontera.pt</div>


    </body></html>`;

    // Guardar no Airtable PRIMEIRO
    // Nota: htmlContent truncado a 95000 chars para respeitar limite Airtable
    const htmlParaGuardar = html.length > 95000 ? html.replace(/data:image\/[^;]+;base64,[^"]+/g, '') : html;
    try {
      await addProposta({
        refCTR: ref.trim().toUpperCase(),
        instaladorId: installer?.id || 'anonimo',
        instaladorNome: insNome || installer?.nome || 'Desconhecido',
        clienteNome,
        obraMorada: obraMorada + (obraLocalidade ? ', ' + obraLocalidade : ''),
        totalEuros: total,
        htmlContent: htmlParaGuardar,
        criadoEm: new Date().toISOString(),
        tipoSistema,
        numInquilinos,
      });

      // Email automático ao cliente quando proposta é gravada
      if (clienteEmail.trim()) {
        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_key: import.meta.env.VITE_WEB3FORMS_KEY,
            to_email: clienteEmail.trim(),
            subject: `AT CONTERA — Tem uma proposta de orçamento disponível`,
            from_name: `${insNome || installer?.nome || 'AT CONTERA'}`,
            message: `Olá ${clienteNome || 'Cliente'},\n\nFoi preparada uma proposta de orçamento para a sua obra.\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nReferência: ${ref.trim().toUpperCase()}\nInstalador: ${insNome || installer?.nome || ''}\nTipo: ${tipoSistema === 'video' ? 'Vídeo Porteiro' : 'Áudio Porteiro'}\nValor total (IVA incl.): ${fmt(total)}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nPara consultar e aprovar a proposta:\n🔗 ${window.location.origin}/#/garantia\n\nIntroduza o seu código: ${ref.trim().toUpperCase()}\n\nDúvidas: 📞 217 967 663 | at@contera.pt\n\nAT CONTERA | Distribuidor Oficial Comelit`,
          }),
        });
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 30000);
    } catch (e) {
      console.error('Erro ao guardar proposta:', e);
      alert('Erro ao guardar a proposta. Tente novamente.');
    }
    setSaving(false);
  }

  const inputCls = "h-8 text-sm";
  const labelCls = "text-xs text-muted-foreground";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/instalador')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={18} />
        </button>
        <FileText size={18} className="text-primary" />
        <h1 className="font-bold text-foreground">Gerador de Proposta de Orçamento</h1>
        {installer && <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg">👤 {installer.nome}</span>}
      </header>

      {/* Bloqueio se não estiver logado */}
      {!installer && (
        <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Lock size={28} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Acesso Restrito</h2>
          <p className="text-muted-foreground text-sm">O gerador de propostas é exclusivo para Instaladores Certificados AT CONTERA. Faça login na área do instalador para continuar.</p>
          <Link to="/instalador">
            <Button className="mt-2">Ir para o Login de Instalador</Button>
          </Link>
        </div>
      )}

      {installer && (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* Sistema */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Calculator size={16} className="text-primary" />
            Sistema & Mão de Obra
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-1.5">
              <Label className={labelCls}>Tipo de sistema</Label>
              <div className="flex gap-2">
                {(['video','audio'] as const).map(t => (
                  <button key={t} type="button"
                    onClick={() => setTipoSistema(t)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${tipoSistema === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                    {t === 'video' ? '📹 Vídeo' : '🔊 Áudio'}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Nº de inquilinos</Label>
              <Input className={inputCls} type="number" min="1" placeholder="Ex: 20"
                value={numInquilinos} onChange={e => setNumInquilinos(e.target.value)} />
            </div>
          </div>
          {laborSugerida && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-green-700 font-medium">Mão de obra sugerida</p>
                <p className="text-xl font-bold text-green-800">{fmt(laborSugerida)}</p>
                <p className="text-xs text-green-600">
                  {parseInt(numInquilinos) > 50
                    ? `Mais de 50 inquilinos — base 1 320 € + ${parseInt(numInquilinos) - 50} × 50 €`
                    : `Para ${numInquilinos} inquilinos · ${tipoSistema === 'video' ? 'Vídeo' : 'Áudio'}`}
                </p>
              </div>
              <Button size="sm" onClick={applyLabor} className="shrink-0">
                Aplicar na proposta
              </Button>
            </div>
          )}
        </div>

        {/* Instalador */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h2 className="font-semibold text-foreground mb-4">Dados da Empresa de Instalação</h2>
          {/* Logo upload */}
          <div className="mb-4 p-3 bg-muted/30 rounded-xl border border-border flex items-center gap-4">
            <div className="shrink-0">
              {logoDataUrl
                ? <img src={logoDataUrl} alt="Logo" className="h-14 w-auto rounded-lg object-contain border border-border bg-white p-1" />
                : <div className="h-14 w-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground text-xs">Logo</div>
              }
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">Logo da Empresa</p>
              <p className="text-xs text-muted-foreground mb-2">Aparece no cabeçalho da proposta (PNG, JPG, SVG)</p>
              <label className="cursor-pointer inline-flex items-center gap-2 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors">
                📎 {logoDataUrl ? 'Alterar logo' : 'Carregar logo'}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogo} />
              </label>
              {logoDataUrl && (
                <button type="button" onClick={() => setLogoDataUrl(null)} className="ml-2 text-xs text-destructive hover:underline">Remover</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Nome completo', insNome, setInsNome],
              ['NIF', insNif, setInsNif],
              ['Morada', insMorada, setInsMorada],
              ['Código Postal', insCp, setInsCp],
              ['Telefone', insTel, setInsTel],
              ['Email', insEmail, setInsEmail],
              ['Nº Cliente AT CONTERA', insNumCliente, setInsNumCliente],
              ['Nº Certificação Comelit', insCertif, setInsCertif],
            ].map(([lbl, val, set]) => (
              <div key={lbl as string} className="space-y-1">
                <Label className={labelCls}>{lbl as string}</Label>
                <Input className={inputCls} value={val as string} onChange={e => (set as (v:string)=>void)(e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        {/* Obra */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h2 className="font-semibold text-foreground mb-4">Dados da Obra</h2>
          <div className="grid grid-cols-2 gap-3">
            {/* CTR separado para mostrar erro */}
            <div className="col-span-2 space-y-1">
              <Label className={labelCls}>Referência AT CONTERA (CTR-...) <span className="text-destructive">*</span></Label>
              <Input className={`${inputCls} ${refError ? 'border-destructive' : ''}`} placeholder="CTR-3F255125"
                value={ref} onChange={e => { setRef(e.target.value); if (e.target.value.toUpperCase().startsWith('CTR-')) setRefError(''); }} />
              {refError && <p className="text-xs text-destructive">{refError}</p>}
            </div>
            {[
              ['Nome do Cliente', clienteNome, setClienteNome],
              ['Email do Cliente', clienteEmail, setClienteEmail],
              ['Morada da Obra', obraMorada, setObraMorada],
              ['Localidade', obraLocalidade, setObraLocalidade],
            ].map(([lbl, val, set]) => (
              <div key={lbl as string} className="space-y-1">
                <Label className={labelCls}>{lbl as string}</Label>
                <Input className={inputCls} value={val as string} onChange={e => (set as (v:string)=>void)(e.target.value)} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className={labelCls}>Tipo de imóvel</Label>
              <select className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background"
                value={tipoImovel} onChange={e => setTipoImovel(e.target.value)}>
                {['Prédio / Apartamento','Moradia','Escritório / Comercial','Condomínio'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className={labelCls}>Data da visita</Label>
              <Input className={inputCls} type="date" value={dataVisita} onChange={e => setDataVisita(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className={labelCls}>Data da proposta</Label>
              <Input className={inputCls} type="date" value={dataProposta} onChange={e => setDataProposta(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className={labelCls}>Válida até</Label>
              <Input className={inputCls} type="date" value={validadeAte} onChange={e => setValidadeAte(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Itens */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Trabalhos e Materiais</h2>
            <Button size="sm" variant="outline" onClick={() => setRows([...rows, { desc:'', qty:1, unit:0 }])}>+ Linha</Button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-1 text-xs text-muted-foreground font-medium px-1">
              <span className="col-span-5">Descrição</span>
              <span className="col-span-2 text-center">Qtd.</span>
              <span className="col-span-2 text-right">Unit. (€)</span>
              <span className="col-span-2 text-right">Total</span>
              <span className="col-span-1"></span>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 items-center">
                <Input className="col-span-5 h-8 text-xs" placeholder="Descrição do item"
                  value={r.desc} onChange={e => updateRow(i,'desc',e.target.value)} />
                <Input className="col-span-2 h-8 text-xs text-center" type="number" min="0"
                  value={r.qty} onChange={e => updateRow(i,'qty',e.target.value)} />
                <Input className="col-span-2 h-8 text-xs text-right" type="number" min="0" step="0.01"
                  value={r.unit || ''} onChange={e => updateRow(i,'unit',e.target.value)} />
                <div className="col-span-2 text-xs text-right font-semibold text-foreground pr-1">
                  {r.qty && r.unit ? fmt(r.qty * r.unit) : '—'}
                </div>
                <button type="button" onClick={() => setRows(rows.filter((_,j) => j !== i))}
                  className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors h-8">
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>IVA 23%</span><span>{fmt(iva)}</span></div>
            <div className="flex justify-between font-bold text-foreground text-base border-t border-border pt-1 mt-1">
              <span>TOTAL</span><span className="text-primary">{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Observações */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h2 className="font-semibold text-foreground mb-3">Observações</h2>
          <textarea className="w-full border border-input rounded-lg p-2 text-sm resize-none h-20 bg-background"
            placeholder="Condições especiais, notas adicionais..."
            value={notas} onChange={e => setNotas(e.target.value)} />
        </div>

        {/* Botão principal — Guardar */}
        <Button onClick={generate} size="lg"
          className={`w-full text-base font-semibold h-12 ${saved ? 'bg-green-600 hover:bg-green-700' : ''}`}
          disabled={saving}>
          {saving
            ? <><span className="animate-spin mr-2">⏳</span>A guardar...</>
            : saved
            ? <>✓ Proposta guardada!</>
            : <><Download size={18} className="mr-2" />Guardar Proposta</>}
        </Button>

        {/* Após guardar — opções */}
        {saved && (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 space-y-1">
              <p className="font-semibold">✅ Proposta guardada com sucesso!</p>
              <p className="text-xs">Código do cliente: <strong className="font-mono">{ref.trim().toUpperCase()}</strong></p>
              <p className="text-xs">Já aparece na sua área, no dashboard AT CONTERA e na Área do Cliente Final.</p>
            </div>

            {/* Enviar por email — opcional */}
            <div className="bg-card border border-border rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-foreground">📧 Enviar por email ao cliente (opcional)</p>
              <div className="flex gap-2">
                <Input
                  className="h-9 text-sm flex-1"
                  placeholder="email@cliente.pt"
                  type="email"
                  value={clienteEmail}
                  onChange={e => setClienteEmail(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 h-9"
                  disabled={!clienteEmail.trim() || saving}
                  onClick={async () => {
                    if (!clienteEmail.trim()) return;
                    setSaving(true);
                    const w3key = import.meta.env.VITE_WEB3FORMS_KEY;
                    await fetch('https://api.web3forms.com/submit', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        access_key: w3key,
                        to_email: clienteEmail.trim(),
                        subject: `AT CONTERA — Proposta de Orçamento ${ref.trim().toUpperCase()}`,
                        from_name: `${insNome || installer?.nome || 'Instalador'} · AT CONTERA`,
                        message: `Olá ${clienteNome || 'Cliente'},\n\nFoi preparada uma proposta de orçamento para a sua obra.\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nReferência: ${ref.trim().toUpperCase()}\nMorada: ${obraMorada}${obraLocalidade ? ', ' + obraLocalidade : ''}\nTipo: ${tipoSistema === 'video' ? 'Vídeo Porteiro' : 'Áudio Porteiro'}\nTotal (IVA incluído): ${fmt(total)}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nPara consultar e aprovar a proposta aceda a:\n${window.location.origin}/#/garantia\n\nIntroduza o código: ${ref.trim().toUpperCase()}\n\nAT CONTERA | comercial@contera.pt`,
                      }),
                    });
                    setSaving(false);
                    setClienteEmail('');
                    alert(`Email enviado para ${clienteEmail.trim()}`);
                  }}
                >
                  Enviar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">O cliente receberá o link e o código para aprovar a proposta online.</p>
            </div>
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground pb-6">
          Ao gravar, a proposta fica disponível para o instalador, AT CONTERA e cliente final com o código CTR.
        </p>
        </div>
      )}
    </div>
  );
}
